<script setup>
import { ref, computed, watch } from 'vue'
import { useEmailTemplatesStore } from '@/stores'
import GrapesEditor from './GrapesEditor.vue'
import EmailTemplateFieldInserter from './EmailTemplateFieldInserter.vue'
import EmailTemplatePreview from './EmailTemplatePreview.vue'

const props = defineProps({
  isDialogVisible: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:isDialogVisible', 'saved'])

const emailTemplatesStore = useEmailTemplatesStore()

// Form state
const saving = ref(false)
const activeTab = ref('editor')
const showPreview = ref(false)
const formValid = ref(false)

// Form fields
const formData = ref({
  templateName: '',
  displayName: '',
  description: '',
  content: '',
  defaultSubject: '',
  defaultPreheader: '',
  dataFieldGroup: 'system',
  defaultFrom: 'preferred',
  defaultCc: [],
  defaultBcc: [],
})

// Allowed sender options (loaded from server) — drives the From/CC/BCC pickers.
const senderOptions = computed(() =>
  emailTemplatesStore.allowedSenders.map(s => ({
    value: s.key,
    title: `${s.displayName} <${s.email}>`,
  })),
)

// Editor ref
const editorRef = ref(null)

// Validation rules
const rules = {
  required: v => !!v || 'Required',
  templateName: v => /^[a-zA-Z0-9_-]+$/.test(v) || 'Only letters, numbers, underscores, and hyphens allowed',
}

// Available field groups
const fieldGroupOptions = [
  { title: 'User', value: 'user' },
  { title: 'Reservation', value: 'reservation' },
  { title: 'User Group', value: 'userGroup' },
  { title: 'Restaurant', value: 'restaurant' },
  { title: 'System', value: 'system' },
  { title: 'Verification', value: 'verification' },
  { title: 'Membership', value: 'membership' },
  { title: 'Partner', value: 'partner' },
]

// Get field group for current template
const currentFieldGroup = computed(() => {
  if (!emailTemplatesStore.fieldGroups) return null
  
  return emailTemplatesStore.fieldGroups[formData.value.dataFieldGroup]
})

// Dialog visibility
const dialogVisible = computed({
  get: () => props.isDialogVisible,
  set: val => emit('update:isDialogVisible', val),
})

// Load field groups on mount
const loadFieldGroups = async () => {
  if (!emailTemplatesStore.fieldGroups) {
    await emailTemplatesStore.getFieldGroups()
  }
}

// Load allowed senders on mount
const loadAllowedSenders = async () => {
  if (!emailTemplatesStore.allowedSenders?.length) {
    await emailTemplatesStore.getAllowedSenders()
  }
}

// Reset form when dialog opens
watch(() => props.isDialogVisible, async visible => {
  if (visible) {
    formData.value = {
      templateName: '',
      displayName: '',
      description: '',
      content: getDefaultContent(),
      defaultSubject: '',
      defaultPreheader: '',
      dataFieldGroup: 'system',
      defaultFrom: emailTemplatesStore.defaultSenderKey || 'preferred',
      defaultCc: [],
      defaultBcc: [],
    }
    await Promise.all([loadFieldGroups(), loadAllowedSenders()])
  }
})

// Get default content for new templates
const getDefaultContent = () => {
  return `<table role="presentation" class="main" style="border-collapse: separate; mso-table-lspace: 0pt; mso-table-rspace: 0pt; background: #ffffff; border-radius: 3px; width: 100%;" width="100%">
  <tr>
    <td class="wrapper" style="font-family: sans-serif; font-size: 14px; vertical-align: top; box-sizing: border-box; padding: 20px;" valign="top">
      <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="border-collapse: separate; mso-table-lspace: 0pt; mso-table-rspace: 0pt; width: 100%;" width="100%">
        <tr>
          <td style="font-family: sans-serif; font-size: 14px; vertical-align: top;" valign="top">
            <p style="font-family: sans-serif; font-size: 14px; font-weight: normal; margin: 0; margin-bottom: 15px;">
              Hello,
            </p>
            <p style="font-family: sans-serif; font-size: 14px; font-weight: normal; margin: 0; margin-bottom: 15px;">
              Your email content goes here. Use template fields like {{ options.user.firstName }} to personalize.
            </p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`
}

// Save template
const saveTemplate = async () => {
  if (!formValid.value) return

  saving.value = true
  try {
    await emailTemplatesStore.createTemplate(formData.value)
    emit('saved')
    dialogVisible.value = false
  } catch (error) {
    console.error('Error creating template:', error)
  } finally {
    saving.value = false
  }
}

// Close dialog
const closeDialog = () => {
  dialogVisible.value = false
}
</script>

<template>
  <VDialog
    v-model="dialogVisible"
    max-width="1800"
    :persistent="saving"
    scrollable
  >
    <VCard class="email-template-dialog">
      <!-- Header -->
      <VCardTitle class="d-flex align-center pa-4">
        <VIcon class="mr-2">
          mdi-email-plus
        </VIcon>
        <span>Create New Email Template</span>
        <VSpacer />
        <VBtn
          icon="mdi-close"
          variant="text"
          @click="closeDialog"
        />
      </VCardTitle>

      <VDivider />

      <!-- Content -->
      <VCardText class="pa-0">
        <VForm v-model="formValid">
          <VRow
            no-gutters
            class="fill-height"
          >
            <!-- Left Column: Editor -->
            <VCol
              cols="12"
              :lg="showPreview ? 8 : 12"
            >
              <div class="pa-4">
                <!-- Template Info -->
                <VRow class="mb-4">
                  <VCol
                    cols="12"
                    md="4"
                  >
                    <VTextField
                      v-model="formData.templateName"
                      label="Template Name *"
                      variant="outlined"
                      density="compact"
                      hint="Unique identifier (e.g., 'welcome-email', 'password_reset')"
                      :rules="[rules.required, rules.templateName]"
                      persistent-hint
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    md="4"
                  >
                    <VTextField
                      v-model="formData.displayName"
                      label="Display Name *"
                      variant="outlined"
                      density="compact"
                      hint="Human-readable name"
                      :rules="[rules.required]"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    md="4"
                  >
                    <VSelect
                      v-model="formData.dataFieldGroup"
                      :items="fieldGroupOptions"
                      label="Data Field Group *"
                      variant="outlined"
                      density="compact"
                      hint="Determines available template fields"
                    />
                  </VCol>
                </VRow>

                <VRow class="mb-4">
                  <VCol
                    cols="12"
                    md="4"
                  >
                    <VTextField
                      v-model="formData.description"
                      label="Description"
                      variant="outlined"
                      density="compact"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    md="4"
                  >
                    <VTextField
                      v-model="formData.defaultSubject"
                      label="Default Subject Line"
                      variant="outlined"
                      density="compact"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    md="4"
                  >
                    <VTextField
                      v-model="formData.defaultPreheader"
                      label="Preheader Text"
                      variant="outlined"
                      density="compact"
                    />
                  </VCol>
                </VRow>

                <!-- Sender Settings -->
                <VRow class="mb-4">
                  <VCol
                    cols="12"
                    md="4"
                  >
                    <VSelect
                      v-model="formData.defaultFrom"
                      label="From"
                      variant="outlined"
                      density="compact"
                      :items="senderOptions"
                      item-title="title"
                      item-value="value"
                      hint="Falls back to system default if unset"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    md="4"
                  >
                    <VSelect
                      v-model="formData.defaultCc"
                      label="CC"
                      variant="outlined"
                      density="compact"
                      multiple
                      chips
                      closable-chips
                      clearable
                      :items="senderOptions"
                      item-title="title"
                      item-value="value"
                      hint="Adds to any dynamic CCs for the event"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    md="4"
                  >
                    <VSelect
                      v-model="formData.defaultBcc"
                      label="BCC"
                      variant="outlined"
                      density="compact"
                      multiple
                      chips
                      closable-chips
                      clearable
                      :items="senderOptions"
                      item-title="title"
                      item-value="value"
                      hint="Overrides the default BCC when set"
                    />
                  </VCol>
                </VRow>

                <!-- Toolbar -->
                <div class="d-flex align-center mb-3">
                  <VBtnToggle
                    v-model="activeTab"
                    mandatory
                    density="compact"
                    class="mr-3"
                  >
                    <VBtn
                      value="editor"
                      size="small"
                    >
                      <VIcon size="small">
                        mdi-pencil
                      </VIcon>
                      <span class="ml-1">Visual Editor</span>
                    </VBtn>
                    <VBtn
                      value="source"
                      size="small"
                    >
                      <VIcon size="small">
                        mdi-code-braces
                      </VIcon>
                      <span class="ml-1">HTML Source</span>
                    </VBtn>
                    <VBtn
                      value="fields"
                      size="small"
                    >
                      <VIcon size="small">
                        mdi-code-tags
                      </VIcon>
                      <span class="ml-1">Fields</span>
                    </VBtn>
                  </VBtnToggle>

                  <VSpacer />

                  <VBtn
                    size="small"
                    :variant="showPreview ? 'flat' : 'outlined'"
                    :color="showPreview ? 'primary' : undefined"
                    @click="showPreview = !showPreview"
                  >
                    <VIcon size="small">
                      mdi-eye
                    </VIcon>
                    <span class="ml-1">Preview</span>
                  </VBtn>
                </div>

                <!-- Editor / Source / Fields Tabs -->
                <VWindow v-model="activeTab">
                  <!-- Visual Editor (GrapeJS) -->
                  <VWindowItem value="editor">
                    <GrapesEditor
                      ref="editorRef"
                      v-model="formData.content"
                      height="500px"
                    />
                  </VWindowItem>

                  <!-- HTML Source -->
                  <VWindowItem value="source">
                    <div class="source-editor-container">
                      <VTextarea
                        v-model="formData.content"
                        variant="outlined"
                        rows="20"
                        class="html-source-textarea"
                        hide-details
                      />
                      <p class="text-caption text-grey mt-2">
                        Edit the raw HTML source. Use LiquidJS syntax:
                        <code v-pre>{{ options.fieldName }}</code> for output,
                        <code v-pre>{% if options.user.verified %}</code> for logic.
                      </p>
                    </div>
                  </VWindowItem>

                  <!-- Field Inserter -->
                  <VWindowItem value="fields">
                    <EmailTemplateFieldInserter
                      :field-group="currentFieldGroup"
                      :group-name="formData.dataFieldGroup"
                    />
                  </VWindowItem>
                </VWindow>
              </div>
            </VCol>

            <!-- Right Column: Preview -->
            <VCol
              v-if="showPreview"
              cols="12"
              lg="4"
              class="preview-column"
            >
              <div class="pa-4 h-100">
                <EmailTemplatePreview
                  :content="formData.content"
                  :data-field-group="formData.dataFieldGroup"
                  :field-group="currentFieldGroup"
                  :preheader="formData.defaultPreheader"
                />
              </div>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>

      <VDivider />

      <!-- Actions -->
      <VCardActions class="pa-4">
        <VSpacer />

        <VBtn
          variant="text"
          :disabled="saving"
          @click="closeDialog"
        >
          Cancel
        </VBtn>

        <VBtn
          color="primary"
          :loading="saving"
          :disabled="!formValid"
          @click="saveTemplate"
        >
          <VIcon start>
            mdi-content-save
          </VIcon>
          Create Template
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.email-template-dialog {
  height: 95vh;
  display: flex;
  flex-direction: column;
}

.preview-column {
  border-left: 1px solid var(--ds-border-default);
  background-color: var(--ds-surface-subtle);
}

.source-editor-container {
  border: 1px solid var(--ds-border-default);
  border-radius: 8px;
  padding: 16px;
  background-color: var(--ds-surface-subtle);
}

.html-source-textarea {
  :deep(textarea) {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
    font-size: 12px !important;
    line-height: 1.5;
  }
}
</style>
